import { identityDelivery } from './identityDelivery';
import { eventContext, legacyEventAttributes } from './eventContext';
import { mapDomainEvent } from '../../contracts/farmart-app/adapter';
import * as builtInContracts from '@farmart-engineering/event-contracts';
import {
	createEventTracker,
	type EventDefinition,
	type EventTracker,
	type TrackOptions,
} from '@farmart-engineering/chronolog-sdk/events';
import {
	createLocalStorageEventStore,
	EventTransportError,
} from '@farmart-engineering/chronolog-sdk/events';
import { createIngestionTransport } from '@farmart-engineering/chronolog-sdk/events/ingestion';
export {
	createCapacitorEventCollector,
	type CapacitorCampaign,
	type CapacitorEventCollector,
} from '@farmart-engineering/chronolog-sdk/events/capacitor';
import { getChronologTraceContext, logger } from '@mono-farmart-web/logger';
import { reportApiError } from '@mono-farmart-web/api-utils/utils/apiCaller/reportApiError';
import {
	getChronologIngestionConfig,
	type ChronologIngestionConfig,
} from './config';
export { chronologMerchantUserId } from './merchantIdentity';

const SOURCE = '/apps/farmart-app';
const EVENT_TYPE = 'com.farmart.analytics.event.v1';
const DATA_SCHEMA =
	'https://prod-analytics.farmartos.com/schemas/chronolog/farmart-app-analytics/v1.json';
const ANONYMOUS_ID_KEY = 'chronolog-anonymous-id';
const SESSION_ID_KEY = 'chronolog-session-id';
const SESSION_LAST_ACTIVE_KEY = 'chronolog-session-last-active-at';
const IDENTIFIED_USER_KEY = 'chronolog-identified-user';
const IDENTITY_LINK_KEY = 'chronolog-identity-link';
const PROVIDER_IDENTITY_LINK_KEY_PREFIX = 'chronolog-provider-identity-link:';
const SESSION_TIMEOUT_MS = 30 * 60 * 1_000;
// Keep the largest normalized batch below the browser fetch keepalive ceiling so
// pagehide flushes remain eligible for background delivery.
const BROWSER_KEEPALIVE_BATCH_SIZE = 10;
export const CHRONOLOG_IDENTITY_CHANGED_EVENT = 'chronolog-identity-changed';

type EventAttributes = Record<string, unknown>;
export type ChronologPlatform = 'web' | 'android' | 'ios';

export interface ChronologRuntimeContext {
	platform: ChronologPlatform;
	appVersion?: string;
	buildNumber?: string;
	os?: string;
	osVersion?: string;
	locale?: string;
	timezone?: string;
}

let runtimeContext: ChronologRuntimeContext = { platform: 'web' };

export interface ChronologEventContext {
	legacyEventName?: string;
	legacyAttributes?: EventAttributes;
	userId?: string;
	platform?: string | null;
	nonInteractive?: boolean;
	sessionId?: string;
}

export interface ChronologIdentifyOptions extends ChronologEventContext {
	traits?: EventAttributes;
}

export interface ChronologProviderIdentityBridgeOptions
	extends ChronologEventContext {
	provider: 'moengage';
	providerDeviceIdSha256: string;
}

export interface ChronologLogoutOptions extends ChronologEventContext {
	reason?: string;
}

export interface ChronologSessionState {
	sessionId: string;
	started: boolean;
}

export interface LegacyAutomaticEvent {
	eventName: string;
	attributes: EventAttributes;
	context?: ChronologEventContext;
}

export type ChronologCollectorMapper = (
	definition: EventDefinition<object>,
	data: object,
	options: TrackOptions,
) => LegacyAutomaticEvent | null;

interface TrackerRuntime {
	key: string;
	tracker: EventTracker;
	recoverIdentityLinks: () => Promise<boolean>;
}

class IngestionUnconfiguredError extends EventTransportError {
	constructor() {
		super('Chronolog ingestion configuration unavailable', false);
	}
}

let trackerRuntime: TrackerRuntime | undefined;
let eventLifecycleInstalled = false;
// A failed write must not expose the previous account in this process.
const identityFallback = new Map<string, string | null>();

export function configureChronologRuntimeContext(
	context: Partial<ChronologRuntimeContext>,
): void {
	runtimeContext = {
		...runtimeContext,
		...context,
		platform: canonicalPlatform(context.platform || runtimeContext.platform),
	};
}

export function chronologAutomaticAttributes(
	attributes: EventAttributes = {},
): EventAttributes {
	return compactRecord({
		...attributes,
		app_version: runtimeContext.appVersion,
		build_number: runtimeContext.buildNumber,
		os: runtimeContext.os,
		os_version: runtimeContext.osVersion,
		locale: runtimeContext.locale,
		timezone: runtimeContext.timezone,
	});
}

export interface ChronologPageView {
	route: string;
	title?: string;
	referrerHost?: string;
	attribution?: Partial<
		Record<
			| 'utm_source'
			| 'utm_medium'
			| 'utm_campaign'
			| 'utm_id'
			| 'utm_content'
			| 'utm_term',
			string
		>
	>;
}

/** Records one router-owned page view with the governed built-in contract. */
export function recordChronologPageView(page: ChronologPageView): boolean {
	const route = page.route.split(/[?#]/)[0];
	const data = {
		route,
		...(page.title ? { title: page.title } : {}),
		...(page.referrerHost ? { referrerHost: page.referrerHost } : {}),
	};
	const tracker = createChronologCollectorAdapter(() => ({
		eventName: 'Viewed Web Page',
		attributes: chronologAutomaticAttributes({
			route,
			title: page.title,
			referrer_host: page.referrerHost,
			provider: 'farmart-router',
			...page.attribution,
		}),
	}));
	return tracker.track(builtInContracts.webPageViewedV1, data);
}

export function beginChronologSession(forceNew = false): ChronologSessionState {
	return resolveSession(true, forceNew);
}

export function getChronologIdentitySnapshot(): {
	userId?: string;
	anonymousId: string;
	sessionId: string;
	platform: ChronologPlatform;
} {
	const storage = localStorageOrNull();
	const anonymousId = persistentId(storage, ANONYMOUS_ID_KEY);
	return compactRecord({
		userId: identifiedUser(storage, anonymousId) || undefined,
		anonymousId,
		sessionId: resolveSession(false).sessionId,
		platform: runtimeContext.platform,
	}) as {
		userId?: string;
		anonymousId: string;
		sessionId: string;
		platform: ChronologPlatform;
	};
}

/**
 * Creates the deliberately small boundary used by v4 automatic collectors.
 * The collector owns normalized semantics and the v4 tracker still owns the
 * CloudEvent envelope, queue, acknowledgement handling, retries and transport.
 * The mapper retains legacy parity during rollout; typed mode sends validated
 * normalized data through the same tracker and transport.
 */
export function createChronologCollectorAdapter(
	mapper: ChronologCollectorMapper,
): Pick<EventTracker, 'track'> {
	return {
		track: (definition, data, options: TrackOptions = {}) => {
			if (typeof definition === 'string') return false;
			const mapped = mapper(definition as EventDefinition<object>, data, options);
			if (!mapped) return false;
			return queueTypedEvent(definition.type, data, {
				...mapped.context,
				sessionId: options.sessionId || mapped.context?.sessionId,
				legacyEventName: mapped.eventName,
				legacyAttributes: mapped.attributes,
			});
		},
	};
}

/** Records the anonymous-to-known-user association once per browser identity. */
export async function identifyChronologUser(
	options: ChronologIdentifyOptions,
): Promise<boolean> {
	const userId = options.userId?.trim();
	if (!userId) return false;

	const storage = localStorageOrNull();
	let anonymousId = persistentId(storage, ANONYMOUS_ID_KEY);
	const previousUser = identifiedUser(storage, anonymousId);
	if (previousUser && previousUser !== userId) {
		// Account changes do not always pass through the explicit logout UI.
		void logoutChronologUser({
			userId: previousUser,
			reason: 'account_switch',
			platform: options.platform,
		});
		anonymousId = persistentId(storage, ANONYMOUS_ID_KEY);
	}
	if (previousUser !== userId) {
		storeIdentifiedUser(storage, anonymousId, userId);
		notifyIdentityChanged();
	}
	// Current identity and delivery bookkeeping are separate. Rejected links
	// retain their exact envelope for recovery, including after logout.
	const runtime = getTrackerRuntime();
	if (!runtime) return false;
	const link = identityLinkValue(anonymousId, userId);
	if (readIdentityValue(storage, IDENTITY_LINK_KEY) === link)
		return runtime.recoverIdentityLinks();

	const accepted = queueTypedEvent(
		'com.farmart.user.identified.v1',
		{ userId, anonymousId },
		{ userId, platform: options.platform },
	);
	if (accepted) {
		writeIdentityValue(storage, IDENTITY_LINK_KEY, link);
	}
	return accepted;
}

/**
 * Records a privacy-safe link between the current Chronolog identity and an
 * analytics provider identity. Raw provider identifiers must be hashed before
 * this boundary and are never accepted here.
 */
export async function recordChronologProviderIdentityBridge(
	options: ChronologProviderIdentityBridgeOptions,
): Promise<boolean> {
	const userId = options.userId?.trim() || getChronologIdentitySnapshot().userId;
	const fingerprint = options.providerDeviceIdSha256.trim().toLowerCase();
	if (!userId || !/^[a-f0-9]{64}$/.test(fingerprint)) return false;

	const storage = localStorageOrNull();
	const anonymousId = persistentId(storage, ANONYMOUS_ID_KEY);
	// A shell may run both native and web provider SDKs, each with its own device
	// identity. Keep their delivery receipts separately so both mappings survive
	// reloads without alternately re-emitting each other.
	const key = `${PROVIDER_IDENTITY_LINK_KEY_PREFIX}${options.provider}:${fingerprint}`;
	const value = JSON.stringify({ anonymousId, userId, fingerprint });
	const runtime = getTrackerRuntime();
	if (!runtime) return false;
	if (readIdentityValue(storage, key) === value)
		return runtime.recoverIdentityLinks();

	const accepted = queueTypedEvent(
		'com.farmart.user.identified.v1',
		{
			userId,
			anonymousId,
			traits: {
				identityProvider: options.provider,
				providerDeviceIdSha256: fingerprint,
				identityEvidence: 'sdk_device_id_at_login',
			},
		},
		{ userId, platform: options.platform },
	);
	if (accepted) writeIdentityValue(storage, key, value);
	return accepted;
}

/** Emits logout before clearing analytics identity and rotating the anonymous id. */
export function logoutChronologUser(
	options: ChronologLogoutOptions = {},
): Promise<boolean> {
	const userId = options.userId?.trim() || getChronologIdentitySnapshot().userId;
	const delivery = Promise.resolve(
		userId
			? queueTypedEvent(
					'com.farmart.user.logged_out.v1',
					{ userId, ...(options.reason ? { reason: options.reason } : {}) },
					{ userId, platform: options.platform },
				)
			: false,
	);
	const storage = localStorageOrNull();
	clearIdentifiedUser(storage);
	writeIdentityValue(storage, IDENTITY_LINK_KEY, null);
	writeIdentityValue(
		storage,
		`${PROVIDER_IDENTITY_LINK_KEY_PREFIX}moengage`,
		null,
	);
	clearIdentityValuesWithPrefix(
		storage,
		`${PROVIDER_IDENTITY_LINK_KEY_PREFIX}moengage:`,
	);
	rotatePersistentId(storage, ANONYMOUS_ID_KEY);
	removePersistentId(storage, SESSION_ID_KEY);
	removePersistentId(storage, SESSION_LAST_ACTIVE_KEY);
	notifyIdentityChanged();
	return delivery;
}

export async function sendChronologEvent(
	eventName: string,
	eventAttributes: EventAttributes,
	context: ChronologEventContext = {},
): Promise<boolean> {
	ensureEventIdentity(context);
	const runtime = getTrackerRuntime();
	if (!runtime) return false;
	const mapped = mapDomainEvent(eventName.trim(), eventAttributes);
	if (mapped.kind === 'invalid') {
		logger.err(new Error('Domain event validation failed'), {
			component: 'chronolog-events',
			phase: 'validation',
			eventType: mapped.type,
			validationPath: mapped.field,
		});
		return false;
	}
	if (mapped.kind === 'typed')
		return queueTypedEvent(
			mapped.definition.type,
			mapped.data,
			{
				...context,
				legacyEventName: eventName,
				legacyAttributes: eventAttributes,
			},
			mapped.definition,
		);
	return queueChronologEvent(eventName, eventAttributes, context, runtime);
}

export async function flushChronologEvents(): Promise<void> {
	await trackerRuntime?.tracker.flush();
}

function queueChronologEvent(
	eventName: string,
	eventAttributes: EventAttributes,
	context: ChronologEventContext = {},
	runtime = getTrackerRuntime(),
): boolean {
	if (!runtime) return false;
	const normalizedEventName = eventName.trim();
	if (!normalizedEventName) return false;

	const config = getChronologIngestionConfig();
	if (!config) return false;
	const storage = localStorageOrNull();
	const anonymousId = persistentId(storage, ANONYMOUS_ID_KEY);
	const sessionId =
		context.sessionId || resolveSession(!context.nonInteractive).sessionId;
	const userId = context.userId || identifiedUser(storage, anonymousId) || '';
	const platform = canonicalPlatform(
		context.platform || runtimeContext.platform,
	);

	return runtime.tracker.track(
		EVENT_TYPE,
		{
			app_name: 'farmart-app',
			environment: config.environment,
			event_name: normalizedEventName,
			event_attributes_json: safeStringify(
				legacyEventAttributes(eventAttributes, runtimeContext.osVersion),
			),
			user_id: userId,
			anonymous_id: anonymousId,
			session_id: sessionId,
			platform,
		},
		{
			subject: userId || anonymousId,
			dataschema: DATA_SCHEMA,
			purpose: 'analytics',
			owner: 'farmart-app',
			schemaVersion: 1,
			sessionId,
			context: compactRecord({
				...eventContext(normalizedEventName, eventAttributes),
				appName: 'farmart-app',
				appVersion: runtimeContext.appVersion,
				platform,
				os: runtimeContext.os,
				osVersion: runtimeContext.osVersion,
				anonymousId,
				userId: userId || undefined,
				locale: runtimeContext.locale,
				timezone: runtimeContext.timezone,
			}) as TrackOptions['context'],
		},
	);
}

const typedDefinitions = new Map<string, EventDefinition<object>>(
	Object.values(builtInContracts).map(definition => [
		definition.type,
		definition,
	]),
);

function identityLinkValue(anonymousId: string, userId: string): string {
	const config = getChronologIngestionConfig();
	return JSON.stringify({
		anonymousId,
		userId,
		endpoint: config?.endpoint,
		environment: config?.environment,
	});
}

function ensureEventIdentity(context: ChronologEventContext): void {
	const identity = getChronologIdentitySnapshot();
	const userId = context.userId?.trim() || identity.userId;
	if (
		userId &&
		(userId !== identity.userId ||
			readIdentityValue(localStorageOrNull(), IDENTITY_LINK_KEY) !==
				identityLinkValue(identity.anonymousId, userId))
	) {
		// Covers early auth callbacks and a previous link that failed to enqueue.
		void identifyChronologUser({ userId, platform: context.platform });
	}
}

function queueTypedEvent(
	type: string,
	data: object,
	context: ChronologEventContext = {},
	suppliedDefinition?: EventDefinition<object>,
): boolean {
	const runtime = getTrackerRuntime();
	const definition = suppliedDefinition ?? typedDefinitions.get(type);
	if (!runtime || !definition) return false;
	if (
		type !== 'com.farmart.user.identified.v1' &&
		type !== 'com.farmart.user.logged_out.v1'
	)
		ensureEventIdentity(context);
	const identity = getChronologIdentitySnapshot();
	const userId = context.userId || identity.userId;
	const sessionId =
		context.sessionId || resolveSession(!context.nonInteractive).sessionId;
	return runtime.tracker.track(definition, data, {
		subject: userId || identity.anonymousId,
		sessionId,
		context: compactRecord({
			...eventContext(context.legacyEventName, context.legacyAttributes),
			appName: 'farmart-app',
			appVersion: runtimeContext.appVersion,
			platform: canonicalPlatform(context.platform || runtimeContext.platform),
			os: runtimeContext.os,
			osVersion: runtimeContext.osVersion,
			anonymousId: identity.anonymousId,
			userId,
			locale: runtimeContext.locale,
			timezone: runtimeContext.timezone,
		}) as TrackOptions['context'],
	});
}

function getTrackerRuntime(): TrackerRuntime | undefined {
	try {
		return createTrackerRuntime();
	} catch {
		logger.err(new Error('Chronolog queue initialization failed'), {
			component: 'chronolog-events',
			phase: 'queue',
		});
		return undefined;
	}
}

function createTrackerRuntime(): TrackerRuntime | undefined {
	const config = getChronologIngestionConfig();
	if (!config) return undefined;
	const key = trackerKey(config);
	if (trackerRuntime?.key === key) return trackerRuntime;
	if (trackerRuntime)
		void trackerRuntime.tracker.shutdown().catch(() => undefined);

	const transport = createIngestionTransport({
		endpoint: config.endpoint,
		fetch: (input, init) => {
			// Recheck on every attempt: a tracker can outlive its configuration.
			if (!getChronologIngestionConfig()) {
				throw new IngestionUnconfiguredError();
			}
			return globalThis.fetch(input, init);
		},
		headers: { Authorization: `Bearer ${config.token}` },
		timeoutMs: 10_000,
	});
	// Namespace excludes credentials and app version so token rotation/upgrades can
	// recover the same queue without crossing endpoint or environment boundaries.
	const storage = localStorageOrNull();
	if (!storage) throw new Error('Persistent event storage unavailable');
	const store = createLocalStorageEventStore(
		storage,
		JSON.stringify([config.endpoint, config.environment, SOURCE]),
	);
	const delivery = identityDelivery(
		storage,
		JSON.stringify([config.endpoint, config.environment, SOURCE]),
		store,
		transport,
	);
	const tracker = createEventTracker({
		store: delivery.store,
		service: {
			name: 'farmart-app',
			version: runtimeContext.appVersion,
			environment: config.environment,
		},
		source: SOURCE,
		transport: delivery.transport,
		batch: {
			size: BROWSER_KEEPALIVE_BATCH_SIZE,
			intervalMs: 5_000,
			maxQueueSize: 1_000,
		},
		maxRetries: 3,
		retryBaseDelayMs: 250,
		getTraceContext: getChronologTraceContext,
		onError: (error, details) => {
			if (error instanceof IngestionUnconfiguredError) return;
			reportApiError(error, {
				component: 'chronolog-events',
				phase: details.phase,
				dropped: details.dropped,
			});
		},
	});
	trackerRuntime = { key, tracker, recoverIdentityLinks: delivery.recover };
	installEventLifecycleFlush();
	return trackerRuntime;
}

function installEventLifecycleFlush(): void {
	if (eventLifecycleInstalled || typeof window === 'undefined') return;
	eventLifecycleInstalled = true;
	window.addEventListener('storage', event => {
		if (
			event.storageArea === localStorageOrNull() &&
			(event.key === null ||
				[ANONYMOUS_ID_KEY, IDENTIFIED_USER_KEY].includes(event.key))
		) {
			notifyIdentityChanged();
		}
	});
	window.addEventListener('online', () => {
		void trackerRuntime?.tracker
			.flush()
			.then(() => trackerRuntime?.recoverIdentityLinks())
			.catch(() => undefined);
	});
	window.addEventListener('pagehide', () => {
		void trackerRuntime?.tracker.flush().catch(() => undefined);
	});
}

function trackerKey(config: ChronologIngestionConfig): string {
	return [
		config.endpoint,
		config.token,
		config.environment,
		runtimeContext.appVersion || '',
	].join('\u0000');
}

function canonicalPlatform(value?: string | null): ChronologPlatform {
	switch (value?.trim().toLowerCase()) {
		case 'android':
		case 'app':
		case 'application':
		case 'capacitor':
			return 'android';
		case 'ios':
			return 'ios';
		default:
			return 'web';
	}
}

function resolveSession(
	touch: boolean,
	forceNew = false,
): ChronologSessionState {
	const storage = localStorageOrNull();
	const now = Date.now();
	try {
		const previous = readIdentityValue(storage, SESSION_ID_KEY);
		const lastActiveAt = Number(
			readIdentityValue(storage, SESSION_LAST_ACTIVE_KEY),
		);
		const expired =
			!Number.isFinite(lastActiveAt) || now - lastActiveAt > SESSION_TIMEOUT_MS;
		const started = forceNew || !previous || expired;
		const sessionId = started ? randomId() : previous;
		if (started) writeIdentityValue(storage, SESSION_ID_KEY, sessionId);
		if (started || touch)
			writeIdentityValue(storage, SESSION_LAST_ACTIVE_KEY, String(now));
		return { sessionId, started };
	} catch {
		return { sessionId: randomId(), started: true };
	}
}

function randomId(): string {
	if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
	return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function persistentId(storage: Storage | null, key: string): string {
	try {
		const existing = readIdentityValue(storage, key);
		if (existing) return existing;
		const created = randomId();
		writeIdentityValue(storage, key, created);
		return created;
	} catch {
		return randomId();
	}
}

function rotatePersistentId(storage: Storage | null, key: string): void {
	writeIdentityValue(storage, key, randomId());
}

function removePersistentId(storage: Storage | null, key: string): void {
	writeIdentityValue(storage, key, null);
}

function readIdentityValue(
	storage: Storage | null,
	key: string,
): string | null {
	if (identityFallback.has(key)) {
		const value = identityFallback.get(key) ?? null;
		writeIdentityValue(storage, key, value);
		return value;
	}
	try {
		return storage?.getItem(key) ?? null;
	} catch {
		return null;
	}
}

function writeIdentityValue(
	storage: Storage | null,
	key: string,
	value: string | null,
): void {
	identityFallback.set(key, value);
	if (!storage) return;
	try {
		if (value === null) storage.removeItem(key);
		else storage.setItem(key, value);
		identityFallback.delete(key);
	} catch {
		// If replacement fails, discard the stale value so a reload cannot
		// resurrect it. Retain the safe replacement in memory until storage recovers.
		try {
			storage.removeItem(key);
		} catch {
			/* Storage access is unavailable. */
		}
	}
}

function clearIdentityValuesWithPrefix(
	storage: Storage | null,
	prefix: string,
): void {
	const keys = new Set(
		Array.from(identityFallback.keys()).filter(key => key.startsWith(prefix)),
	);
	if (storage) {
		try {
			for (let index = 0; index < storage.length; index += 1) {
				const key = storage.key(index);
				if (key?.startsWith(prefix)) keys.add(key);
			}
		} catch {
			// The in-memory receipts are still cleared when storage is unavailable.
		}
	}
	for (const key of keys) writeIdentityValue(storage, key, null);
}

function identifiedUser(
	storage: Storage | null,
	anonymousId: string,
): string | null {
	try {
		const raw = readIdentityValue(storage, IDENTIFIED_USER_KEY);
		if (!raw) return null;
		const value: unknown = JSON.parse(raw);
		if (!isRecord(value)) return null;
		return value.anonymousId === anonymousId && typeof value.userId === 'string'
			? value.userId
			: null;
	} catch {
		return null;
	}
}

function storeIdentifiedUser(
	storage: Storage | null,
	anonymousId: string,
	userId: string,
): void {
	writeIdentityValue(
		storage,
		IDENTIFIED_USER_KEY,
		JSON.stringify({ anonymousId, userId }),
	);
}

function clearIdentifiedUser(storage: Storage | null): void {
	removePersistentId(storage, IDENTIFIED_USER_KEY);
}

function localStorageOrNull(): Storage | null {
	if (typeof window === 'undefined') return null;
	try {
		return window.localStorage;
	} catch {
		return null;
	}
}

function safeStringify(value: EventAttributes): string {
	try {
		return JSON.stringify(value);
	} catch {
		return '{}';
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function compactRecord(value: EventAttributes): EventAttributes {
	return Object.fromEntries(
		Object.entries(value).filter(
			([, entry]) => entry !== undefined && entry !== '',
		),
	);
}

function notifyIdentityChanged(): void {
	try {
		window.dispatchEvent(new Event(CHRONOLOG_IDENTITY_CHANGED_EVENT));
	} catch {
		// Native identity synchronization is unavailable during SSR.
	}
}
